import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import {
  loadLocaleNamespace,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  CONSUMER_NAMESPACES,
  isSupportedLanguage,
  type Namespace,
  type SupportedLanguage,
} from "@pulse/i18n";

export const LANGUAGE_STORAGE_KEY = "pulse.language.consumer";

const LEGACY_LANGUAGE_KEY = "pulse.lang";
const LEGACY_UNIFIED_KEY = "pulse.language";

const DEFAULT_NS = [...CONSUMER_NAMESPACES];

function normalizeLng(raw: string | null | undefined): SupportedLanguage {
  const code = raw?.slice(0, 2);
  return isSupportedLanguage(code) ? code : DEFAULT_LANGUAGE;
}

/** Peek stored/detected language without waiting for i18n init (SSR-safe). */
export function peekConsumerLanguage(): SupportedLanguage {
  if (typeof window !== "undefined") {
    try {
      const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored) return normalizeLng(stored);
    } catch {
      /* ignore */
    }
    try {
      return normalizeLng(window.navigator.language);
    } catch {
      /* ignore */
    }
  }
  return DEFAULT_LANGUAGE;
}

type BackendCallback = (err: Error | null, data: Record<string, unknown> | boolean) => void;

/** i18next backend that dynamic-imports locale JSON per language/namespace. */
const localeBackend = {
  type: "backend" as const,
  init() {},
  read(lng: string, ns: string, callback: BackendCallback) {
    const language = normalizeLng(lng);
    if (!(DEFAULT_NS as readonly string[]).includes(ns) && ns !== "admin") {
      callback(new Error(`Unknown namespace: ${ns}`), false);
      return;
    }
    void loadLocaleNamespace(language, ns as Namespace)
      .then((data) => callback(null, data))
      .catch((err: unknown) =>
        callback(err instanceof Error ? err : new Error(String(err)), false),
      );
  },
};

// One-time migration to pulse.language.consumer.
if (typeof window !== "undefined") {
  try {
    const legacyLang = window.localStorage.getItem(LEGACY_LANGUAGE_KEY);
    if (legacyLang) {
      if (!window.localStorage.getItem(LANGUAGE_STORAGE_KEY)) {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, legacyLang);
      }
      window.localStorage.removeItem(LEGACY_LANGUAGE_KEY);
    }
    const legacyUnified = window.localStorage.getItem(LEGACY_UNIFIED_KEY);
    if (legacyUnified && !window.localStorage.getItem(LANGUAGE_STORAGE_KEY)) {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, legacyUnified);
    }
    if (legacyUnified) {
      window.localStorage.removeItem(LEGACY_UNIFIED_KEY);
    }
  } catch {
    /* ignore storage access errors */
  }
}

const initPromise: Promise<typeof i18n> = i18n.isInitialized
  ? Promise.resolve(i18n)
  : i18n
      .use(localeBackend)
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        partialBundledLanguages: true,
        fallbackLng: DEFAULT_LANGUAGE,
        supportedLngs: [...SUPPORTED_LANGUAGES],
        ns: DEFAULT_NS,
        defaultNS: "common",
        load: "currentOnly",
        detection: {
          order: ["localStorage", "navigator"],
          caches: ["localStorage"],
          lookupLocalStorage: LANGUAGE_STORAGE_KEY,
        },
        interpolation: { escapeValue: false },
        react: { useSuspense: false },
      })
      .then(() => i18n);

/** Await until i18n finished initial backend load for the active language. */
export async function ensureI18nReady(): Promise<typeof i18n> {
  await initPromise;
  return i18n;
}

/**
 * Load admin namespace for the current (or given) language + fallback.
 * Call when entering `/pulse-ops-x7k2/*` — never from Discovery startup.
 */
export async function ensureAdminI18n(lng?: string): Promise<void> {
  await ensureI18nReady();
  const language = normalizeLng(lng ?? i18n.resolvedLanguage ?? i18n.language);
  const codes = new Set<SupportedLanguage>([language, DEFAULT_LANGUAGE]);
  await Promise.all(
    [...codes].map(async (code) => {
      if (i18n.hasResourceBundle(code, "admin")) return;
      const data = await loadLocaleNamespace(code, "admin");
      i18n.addResourceBundle(code, "admin", data, true, true);
    }),
  );
  const ns = i18n.options.ns;
  if (Array.isArray(ns) && !ns.includes("admin")) {
    i18n.options.ns = [...ns, "admin"];
  }
}

/** Change language and wait until namespaces are loaded (avoids empty UI). */
export async function changeConsumerLanguage(lng: SupportedLanguage): Promise<void> {
  await ensureI18nReady();
  await i18n.changeLanguage(lng);
}

export default i18n;
export type { SupportedLanguage } from "@pulse/i18n";
